import React from 'react';

import { requiredFieldArrayCheckbox } from '../../../util/validators';
import { FieldCheckboxGroup } from '../../../components';
import { LEGAL_DOCS } from '../../../config/legal';

import css from './TermsAndConditions.module.css';

// A link to an in-repo legal page, opened in a new tab so the signup form state is preserved. The
// href + link text come from the single legal config, so the checkbox always points at the exact
// document (and version) the acceptance record captures.
//
// The links live inside the checkbox's <label>, so a left-click would trigger the label's default
// action (toggling the checkbox). preventDefault cancels that — which also cancels the link's own
// navigation — so we open the doc in a new tab ourselves. (Middle/right-click still use the href.)
const openLegalDoc = href => e => {
  e.preventDefault();
  e.stopPropagation();
  if (typeof window !== 'undefined') {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
};

const legalLink = (doc, className) => (
  <a
    className={className}
    href={doc.path}
    target="_blank"
    rel="noopener noreferrer"
    onClick={openLegalDoc(doc.path)}
  >
    {doc.title}
  </a>
);

/**
 * The signup legal-acceptance checkbox. Ticking it is required and records a versioned acceptance of
 * the Terms of Service, Privacy Policy, and Cancellation & Refund Policy (see
 * AuthenticationPage.helpers.js → getExtendedDataMaybe, which turns the `terms` value into
 * protectedData.legalAcceptances).
 *
 * @component
 * @param {Object} props
 * @param {string} props.formId - The form id
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const TermsAndConditions = props => {
  const { formId, intl } = props;

  const termsLink = legalLink(LEGAL_DOCS.termsOfService, css.termsLink);
  const privacyLink = legalLink(LEGAL_DOCS.privacyPolicy, css.privacyLink);
  const cancellationLink = legalLink(LEGAL_DOCS.cancellationPolicy, css.termsLink);

  return (
    <div className={css.root}>
      <FieldCheckboxGroup
        name="terms"
        id={formId ? `${formId}.terms-accepted` : 'terms-accepted'}
        optionLabelClassName={css.finePrint}
        options={[
          {
            key: 'tos-and-privacy',
            label: intl.formatMessage(
              { id: 'AuthenticationPage.legalAcceptText' },
              { termsLink, privacyLink, cancellationLink }
            ),
          },
        ]}
        validate={requiredFieldArrayCheckbox(
          intl.formatMessage({ id: 'AuthenticationPage.termsAndConditionsAcceptRequired' })
        )}
      />
    </div>
  );
};

export default TermsAndConditions;
